export const translations = {
  en: {
    // Header
    header: {
      products: "Products",
      pricing: "Pricing", 
      useCases: "Use Cases",
      docs: "Docs",
      documentation: "Documentation",
      blog: "Blog",
      about: "About",
      careers: "Careers",
      getStarted: "Get Started",
      chinese: "中文",
      login: "Login",
      dashboard: "Dashboard",
      dropdown: {
        products: {
          residentialProxies: "Residential Proxies",
          mobileProxies: "Mobile Proxies"
        },
        useCases: {
          proxiesForAI: "Proxies for AI",
          webScraping: "Web Scraping",
          websiteTesting: "Website Testing",
          travelFareAggregation: "Travel Fare Aggregation",
          marketResearch: "Market Research",
          googleProxies: "Google Proxies",
          accessRestrictedContent: "Access Restricted Content",
          privacyAdvantages: "Privacy Advantages"
        },
        pricing: {
          residentialProxies: "Residential Proxies",
          mobileProxies: "Mobile Proxies"
        }
      }
    },
    
    // Hero Section
    hero: {
      title1: "Enterprise Mobile &",
      title2: "Residential Proxies",
      subtitle: "Access 300M+ IP addresses across 200+ countries with 99.9% uptime. Perfect for web scraping, data collection, and automation at scale.",
      startTrial: "Start Now",
      viewPricing: "View Pricing",
      customers: "10,000+ customers worldwide",
      countries: "200+ countries & 1000+ cities",
      uptime: "99.9% uptime guarantee",
      ipsWorldwide: "300M+ IPs worldwide",
      responseTime: "<500ms response time",
      enterpriseSecurity: "Enterprise Security",
      soc2Monitoring: "SOC2 compliant with 24/7 monitoring",
      simpleTransparentPricing: "Simple, Transparent Pricing"
    },

    // Proxies Pages
    proxies: {
      main: {
        hero: {
          title1: "Premium Global",
          title2: "Proxy Network",
          subtitle: "Access the world's most reliable Residential and Mobile proxy network. 300M+ ethically sourced IPs with enterprise-grade performance."
        }
      },
      residential: {
        hero: {
          title1: "Residential Proxies",
          title2: "with Rotating IPs",
          subtitle: "A cost-effective proxy solution built for any business project. Ethical, fast residential proxies designed to minimize CAPTCHAs and blocks—without stretching your budget."
        },
        standOut: {
          title: "How IP9 Residential Proxies Stand Out",
          content: [
            "IP9 delivers high-quality residential proxies with strong success rates at competitive pricing. Our ethically sourced residential IPs cover 190+ countries, each associated with a real household connection and legitimate Internet Service Provider. Privacy, stability, and performance are at the core of our network.",
            "Our rotating residential proxies include all essential features needed by small and medium-sized businesses as well as individual users. Whether you’re accessing geo-restricted content, running large-scale web scraping, or verifying digital ads, IP9 provides reliable and affordable residential proxy solutions.",
            "With a global network, non-expiring traffic, and flexible pricing, IP9 makes premium residential IPs accessible to everyone."
          ]
        },
        features: {
          title: "Key Features",
          subtitle: "Everything you need for successful data collection and automation.",
          list: [
            { title: "Advanced sticky sessions", description: "Define how long your session lasts and keep the same IP anywhere from 1 second up to 7 days, ensuring a consistent and stable online identity." },
            { title: "Non-expiring traffic", description: "Enjoy a pay-as-you-go model with traffic that never expires—no monthly renewals or usage pressure." },
            { title: "Seamless compatibility", description: "Use IP9 proxies with any software thanks to full HTTP, HTTPS, and SOCKS5 support, ensuring smooth performance across all workflows." },
            { title: "Custom IP rotation", description: "Rotate IPs on every request or maintain the same residential IP for sessions lasting between 1 second and 7 days." },
            { title: "Precise geo-targeting", description: "Target 190+ countries, with the ability to narrow down to specific states or cities at no additional cost—ideal for localized content access and research." }
          ]
        },
        whyChoose: {
          title: "Why Choose IP9?",
          content: [
            "If you need global residential proxies for daily online operations, IP9 is a reliable choice. We provide real residential IP addresses in nearly every country worldwide, all sourced ethically and legally, while keeping pricing highly competitive.",
            "Purchase exactly the amount of traffic you need with our flexible pay-as-you-go plan and use it whenever you like. With non-expiring traffic, volume discounts, and budget-friendly rates, IP9 is designed to scale with your needs.",
            "From long-lasting sticky sessions and dual authentication to full protocol support (HTTP/HTTPS/SOCKS5), IP9 residential proxies are built to handle a wide range of use cases—including web scraping, market research, ad verification, SEO tasks, and bypassing geo-restrictions."
          ]
        },
        advantages: {
          title: "Additional Advantages",
          list: [
            { title: "Best value proxy servers", description: "Access enterprise-grade residential proxies with advanced features—without the premium price tag." },
            { title: "24/7 expert support", description: "Questions or issues? Our support team is available around the clock to assist you whenever you need help." }
          ]
        }
      },
      mobile: {
        hero: {
          title1: "High Performance",
          title2: "Mobile Proxies",
          subtitle: "Real 4G/5G mobile connections from major carriers worldwide. Perfect for account management, social media, and app testing."
        },
        features: {
          title: "Mobile 4G/5G Power",
          subtitle: "Leverage the trust of mobile carrier networks for your most sensitive operations.",
          list: [
            { title: "Real Carrier IPs", description: "Connections via real SIM cards from Verizon, AT&T, Vodafone, and more." },
            { title: "Auto-Rotation", description: "Automatically rotate IP on set intervals or on demand via API." },
            { title: "Highest Trust Score", description: "Mobile IPs carry the highest trust scores, making them immune to most blocks." },
            { title: "Unlimited Bandwidth", description: "Enjoy unmetered bandwidth on our dedicated mobile ports." }
          ]
        }
      }
    },

    // Stats Section
    stats: {
      ipAddresses: "IP Addresses",
      countries: "Countries", 
      uptime: "Uptime",
      support: "Support"
    },

    // Features Section
    features: {
      title: "Why Choose",
      subtitle: "Enterprise-grade proxy infrastructure designed for scale, reliability, and performance. Trusted by leading companies worldwide.",
      features: [
        {
          title: "Global IP Network",
          description: "Access over 300 million IP addresses from 200+ countries and 1000+ cities worldwide."
        },
        {
          title: "99.9% Uptime",
          description: "Enterprise-grade infrastructure ensures maximum reliability for your critical operations."
        },
        {
          title: "Lightning Fast",
          description: "High-speed connections with sub-second response times for optimal performance."
        },
        {
          title: "Advanced Targeting",
          description: "Target by country, city, state, ZIP code, ASN, and even specific coordinates."
        },
        {
          title: "Session Control",
          description: "Maintain sticky sessions for up to 24 hours with full session management control."
        },
        {
          title: "24/7 Support",
          description: "Expert support team available around the clock to help with integration and issues."
        }
      ]
    },

    // Use Cases Section  
    useCases: {
      title1: "Powerful",
      title2: "Use Cases", 
      subtitle: "From AI training to market research, our proxy solutions power diverse industries with reliable, scalable infrastructure.",
      exploreAll: "Explore All Use Cases",
      cases: [
        {
          title: "Web Scraping",
          description: "Extract data from any website without getting blocked or rate-limited."
        },
        {
          title: "Market Research", 
          description: "Gather competitive intelligence and pricing data from global markets."
        },
        {
          title: "Ad Verification",
          description: "Verify ad placements and monitor campaigns across different locations."
        },
        {
          title: "Price Monitoring",
          description: "Track pricing changes and competitor strategies in real-time."
        },
        {
          title: "Social Media",
          description: "Manage multiple accounts and automate social media operations safely."
        },
        {
          title: "SEO Monitoring",
          description: "Track search rankings and monitor SEO performance globally."
        }
      ]
    },

    // Pricing Preview Section
    pricingPreview: {
      title1: "Simple,",
      title2: "Transparent Pricing",
      subtitle: "Scale with confidence using our enterprise-grade infrastructure. Volume discounts up to 31% off.",
      noSetupFees: "No setup fees",
      instantActivation: "Instant activation",
      support247: "24/7 support",
      mostPopular: "MOST POPULAR",
      residentialProxies: "Residential Proxies",
      residentialSubtitle: "Premium residential IPs with global coverage",
      unlimitedMobile: "Unlimited Mobile Proxies",
      mobileSubtitle: "True mobile IPs with carrier rotation",
      professional: "Professional",
      perGB: "per GB",
      save: "Save",
      starter: "Starter",
      business: "Business",
      scale: "Scale",
      trial: "Trial",
      monthly: "Monthly",
      enterprise: "Enterprise",
      perProxy24h: "per proxy/24h",
      perProxyMonth: "per proxy/month",
      contactSales: "contact sales",
      viewAllPlans: "View All Plans",
      getTrial: "Get Trial",
      viewAllMobilePlans: "View All Mobile Plans",
      volumePricingDiscounts: "Volume Pricing Discounts",
      off: "off",
      scaleWithConfidence: "Scale with confidence. The more you use, the more you save.",
      viewAllPricing: "View All Pricing",
      contactSales: "Contact Sales",
      features: {
        unlimitedSessions: "Unlimited Concurrent Sessions",
        freeGeoTargeting: "Free geo-targeting",
        support247: "24/7 support",
        noHiddenFees: "No hidden fees",
        ipQualityEngine: "IP quality assurance engine",
        responseTime: "0.5s response time"
      },
      countriesAvailable: "52+ Countries Available",
      accessProxiesWorldwide: "Access proxies from major countries worldwide with city-level targeting",
      viewAllLocations: "View All Locations",
      cities: "cities",
      countries: {
        "United States": "United States",
        "United Kingdom": "United Kingdom", 
        "Germany": "Germany",
        "France": "France",
        "Canada": "Canada",
        "Australia": "Australia",
        "Japan": "Japan",
        "Brazil": "Brazil"
      },
      volumeRange1: "1-30GB",
      volumeRange2: "30GB - 100GB", 
      volumeRange3: "100GB-1TB",
      volumeRange4: "1-5TB",
      mobilePlans: {
        trial: {
          name: "Trial",
          volume: "24 hours",
          unit: "per proxy/24h"
        },
        monthly: {
          name: "Monthly",
          volume: "30 days",
          unit: "per proxy/month"
        },
        enterprise: {
          name: "Enterprise",
          volume: "Volume pricing",
          unit: "contact sales"
        }
      },
      custom: "Custom",
      upTo40: "Up to 40%"
    },

    // Integrations Section
    integrations: {
      title1: "Developer",
      title2: "Integrations", 
      subtitle: "Step-by-step integration guides for popular tools and frameworks. Get your proxy setup running in minutes with our comprehensive tutorials.",
      tutorials: "Tutorials",
      categories: "Categories",
      updated: "Updated",
      readGuide: "Read Guide",
      viewAllIntegrations: "View All Integrations",
      readyInMinutes: "Ready in Minutes",
      popular: "Popular",
      browseByCategory: "Browse by Category",
      ctaTitle: "Ready to integrate IP9 proxies?",
      ctaSubtitle: "Explore our complete collection of integration tutorials and get your proxies working with your favorite tools in minutes.",
      browseAllTutorials: "Browse All Tutorials",
      viewPricing: "View Pricing",
      readTime: "min read",
      articles: [
        {
          title: "How to Use IP9 Proxies with Python Requests Library",
          excerpt: "Complete guide to integrating IP9 residential and mobile proxies with Python requests for web scraping and API calls."
        },
        {
          title: "Selenium WebDriver with IP9 Proxies: Complete Setup Guide", 
          excerpt: "Learn how to configure Selenium WebDriver with IP9 proxies for automated browser testing and web scraping."
        },
        {
          title: "Building Scrapy Spiders with IP9 Proxy Rotation",
          excerpt: "Master Scrapy spider development with IP9 proxy rotation for large-scale web scraping projects."
        },
        {
          title: "Node.js Axios HTTP Client with IP9 Proxies",
          excerpt: "Complete guide to using IP9 proxies with Node.js Axios for API calls and web scraping."
        },
        {
          title: "Configuring Postman with IP9 Proxies for API Testing",
          excerpt: "Step-by-step guide to set up IP9 proxies in Postman for secure API testing and development."
        },
        {
          title: "Master cURL with IP9 Proxies: Essential Commands",
          excerpt: "Essential cURL commands and techniques for using IP9 proxies in command-line HTTP operations."
        },
        {
          title: "Beautiful Soup Web Scraping with IP9 Proxy Integration",
          excerpt: "Comprehensive guide to web scraping with Beautiful Soup and IP9 proxies for reliable data extraction."
        },
        {
          title: "Building Chrome Extensions with IP9 Proxy Support",
          excerpt: "Create powerful Chrome extensions that leverage IP9 proxies for enhanced functionality and privacy."
        },
        {
          title: "API Testing with Insomnia and IP9 Proxies",
          excerpt: "Advanced API testing techniques using Insomnia REST client with IP9 proxy integration for comprehensive testing."
        }
      ]
    },

    // Testimonials Section
    testimonials: {
      title: "Trusted by Industry Leaders",
      subtitle: "Join thousands of companies using IP9 proxies to power their data operations.",
      at: "at",
      testimonials: [
        {
          name: "Sarah Chen",
          role: "Data Engineering Lead",
          company: "TechCorp",
          content: "IP9's mobile proxies have been game-changing for our data collection. The reliability and speed are unmatched.",
          rating: 5
        },
        {
          name: "Mike Rodriguez", 
          role: "CTO",
          company: "DataFlow Inc",
          content: "We've tried many proxy providers, but IP9's customer support and uptime guarantee set them apart.",
          rating: 5
        },
        {
          name: "Lisa Wang",
          role: "VP of Engineering", 
          company: "ScaleData",
          content: "The geographic targeting capabilities are exactly what we needed for our global market research.",
          rating: 5
        }
      ]
    },

    // FAQ Section
    faq: {
      title: "Frequently Asked Questions",
      subtitle: "Everything you need to know about IP9 proxies.",
      stillHaveQuestions: "Still have questions? Our support team is here to help.",
      contactSupport: "Contact Support",
      scheduleCall: "Schedule a Call",
      faqs: [
        {
          question: "What's the difference between residential and mobile proxies?",
          answer: "Residential proxies use IP addresses from home internet connections, while mobile proxies use IPs from mobile carriers. Mobile proxies are ideal for social media and mobile app testing, offering higher success rates for mobile-specific use cases."
        },
        {
          question: "Do you offer a free trial?",
          answer: "Yes! We offer a $10 trial for mobile proxies (24-hour access) and residential proxy trials. Contact our support team at support@ip9.io to get started."
        },
        {
          question: "What locations do you support?",
          answer: "We support 200+ countries and 1000+ cities worldwide, including major markets like US, UK, Germany, Japan, Australia, and many more. You can target by country, city, state, or even specific ZIP codes."
        },
        {
          question: "How reliable are your proxies?",
          answer: "We guarantee 99.9% uptime with enterprise-grade infrastructure. Our proxy network is constantly monitored and optimized for maximum reliability and performance."
        },
        {
          question: "What's your pricing model?",
          answer: "We offer flexible pricing based on usage. Residential proxies start at $1.3/GB with volume discounts. Mobile proxies start at $10 for a 24-hour trial. Contact us for enterprise pricing."
        },
        {
          question: "Do you provide customer support?",
          answer: "Yes, we provide 24/7 customer support via email, live chat, and dedicated account managers for enterprise customers. Our team is here to help with setup, integration, and any issues."
        }
      ]
    },

    // CTA Section
    cta: {
      title: "Ready to Scale Your Business?",
      subtitle: "Join 10,000+ businesses using IP9 for reliable, high-performance proxy infrastructure. Start your free trial today and experience the difference.",
      startTrial: "Start Now",
      contactSales: "Contact Sales",
      soc2Compliant: "SOC2 Compliant",
      uptimeSLA: "99.9% Uptime SLA",
      support247: "24/7 Support",
      enterpriseSolutions: "Enterprise Solutions",
      enterpriseDescription: "Custom proxy infrastructure for large-scale operations with dedicated support.",
      talkToSales: "Talk to Sales",
      developerResources: "Developer Resources", 
      developerDescription: "Comprehensive API documentation, SDKs, and integration guides.",
      viewDocs: "View Docs"
    },

    // Footer
    footer: {
      description: "Enterprise-grade mobile and residential proxies for reliable data collection at scale.",
      products: {
        title: "Products",
        items: [
          "Residential Proxies",
          "Mobile Proxies", 
          "API Access"
        ]
      },
      useCases: {
        title: "Use Cases",
        items: [
          "Web Scraping",
          "Market Research",
          "Ad Verification", 
          "Price Monitoring",
          "Social Media",
          "SEO Tools"
        ]
      },
      company: {
        title: "Company", 
        items: [
          "About Us",
          "Careers",
          "Privacy Policy",
          "Terms of Service"
        ]
      },
      support: {
        title: "Support",
        items: [
          "Contact"
        ]
      },
      contact: {
        email: "Email us: support@ip9.io",
        address: "128 City Road, London, EC1V 2NX, United Kingdom"
      },
      copyright: "© 2026 KOKLACS LTD. All rights reserved.",
      rightsReserved: "All rights reserved.",
      cookieSettings: "Cookie settings",
      guides: "Guides",
      weAccept: "We accept",
      blog: "Blog",
      legal: {
        title: "Legal",
        legalNotice: "Legal Notice",
        terms: "Terms of Service",
        privacy: "Privacy Policy",
        cookies: "Cookie Policy",
        refund: "Refund Policy",
        aup: "Acceptable Use"
      },
      companyLine: {
        tradingNameOf: "is a trading name of",
        registeredIn: "a company registered in England and Wales",
        companyNo: "company number",
        registeredOffice: "Registered office"
      },
      followUs: "Follow us",
      privacyPolicy: "Privacy Policy",
      termsOfService: "Terms of Service"
    },



    // Cookie banner
    cookies: {
      title: "Cookies",
      text: "We use strictly necessary storage to run this site. With your permission we would also use analytics cookies (Google Analytics, PostHog) to understand how the site is used, and marketing cookies (Meta Pixel) to measure our ads on Facebook and Instagram. You can change your choice at any time.",
      policyLink: "Cookie Policy",
      accept: "Accept all",
      reject: "Reject all",
      customise: "Customise choices",
      save: "Save my choices",
      analyticsLabel: "Analytics",
      analyticsText: "Google Analytics and PostHog: aggregated usage statistics and session replays with form inputs masked.",
      marketingLabel: "Marketing",
      marketingText: "Meta Pixel: measures visits from our Facebook and Instagram ads and lets us show relevant ads on Meta."
    },

    // Contact Page
    contact: {
      title: "Contact Us",
      subtitle: "Get in touch with our team",
      description: "We're here to help you succeed with IP9's proxy solutions. Reach out to us through any of the channels below and we'll get back to you as soon as possible.",
      methods: {
        title: "How Can We Help?",
        subtitle: "Choose the best way to reach us based on your needs",
        support: {
          title: "Technical Support",
          subtitle: "Get help with integration and troubleshooting",
          description: "Our technical team is ready to assist with API integration, configuration issues, and troubleshooting. Perfect for developers and technical users.",
          responseTime: "Response within 2-4 hours"
        },
        sales: {
          title: "Sales & Enterprise",
          subtitle: "Discuss pricing and enterprise solutions",
          description: "Connect with our sales team for custom pricing, enterprise features, dedicated support, and volume discounts tailored to your business needs.",
          responseTime: "Response within 1-2 hours"
        },
        telegram: {
          title: "Telegram Support",
          subtitle: "Quick chat for urgent matters",
          description: "Get immediate assistance through Telegram for urgent issues, quick questions, and real-time support from our team.",
          responseTime: "Response within 30 minutes"
        }
      },
      businessHours: {
        title: "Business Hours",
        weekdays: "Monday - Friday",
        weekends: "Saturday - Sunday",
        note: "For urgent matters outside business hours, use Telegram or email urgent@ip9.io"
      },
      location: {
        title: "Our Location",
        headquarters: "Headquarters",
        timezone: "Time Zone",
        coverage: "Global Coverage",
        coverageDescription: "Registered in London, United Kingdom, we provide 24/7 support and global proxy infrastructure."
      },
      supportCategories: {
        title: "What We Can Help With",
        subtitle: "Our support team covers all aspects of your proxy needs",
        technical: {
          title: "Technical Support",
          items: [
            "API integration assistance",
            "Configuration troubleshooting",
            "Performance optimization",
            "Authentication setup",
            "Custom implementations"
          ]
        },
        billing: {
          title: "Billing & Account",
          items: [
            "Payment and billing questions",
            "Plan upgrades and changes",
            "Usage monitoring",
            "Invoice and receipt requests",
            "Account management"
          ]
        },
        sales: {
          title: "Sales & Enterprise",
          items: [
            "Custom pricing quotes",
            "Enterprise features",
            "Volume discounts",
            "Dedicated support",
            "SLA agreements"
          ]
        }
      },
      emergency: {
        title: "Emergency Contact",
        description: "For critical service issues affecting your business operations, contact us immediately at:"
      }
    },

    // SEO Meta Data
    seo: {
      siteTitle: "IP9",
      home: {
        title: "Home",
        description: "Enterprise-grade mobile and residential proxy services with 300M+ IPs across 200+ countries. 99.9% uptime, fast speeds, and reliable data collection solutions."
      },
      proxies: {
        title: "Proxy Products",
        description: "Explore our premium Residential and Mobile proxy solutions. 300M+ ethically sourced IPs with enterprise-grade performance."
      },
      "residential-proxies": {
        title: "Residential Proxies",
        description: "Enterprise-grade residential proxies with 99.9% success rate. Access 300M+ real user IPs for web scraping and data collection."
      },
      "mobile-proxies": {
        title: "Mobile Proxies",
        description: "High-performance 4G/5G mobile proxies. Real carrier IPs from major networks worldwide for app testing and account management."
      },
      pricing: {
        title: "Pricing",
        description: "Transparent pricing for mobile and residential proxies. Choose from flexible plans starting at $1.30/GB with volume discounts and enterprise solutions available."
      },
      useCases: {
        title: "Use Cases",
        description: "Discover how IP9's proxy infrastructure powers web scraping, market research, ad verification, price monitoring, and more for businesses worldwide."
      },
      docs: {
        title: "Documentation",
        description: "Complete API documentation, integration guides, and technical resources for IP9's proxy services. Get started with our comprehensive developer docs."
      },
      about: {
        title: "About",
        description: "Learn about IP9's mission to provide reliable, high-performance proxy infrastructure for businesses worldwide. Trusted by 10,000+ companies globally."
      },
      contact: {
        title: "Contact",
        description: "Get in touch with IP9's support team. Technical support, sales inquiries, and enterprise solutions. Multiple contact methods with fast response times."
      },
      privacyPolicy: {
        title: "Privacy Policy",
        description: "How KOKLACS LTD (IP9) collects, uses and protects personal data under the UK GDPR and EU GDPR, and how to exercise your rights."
      },
      cookiePolicy: {
        title: "Cookie Policy",
        description: "Which cookies and similar technologies the IP9 website uses, why, and how to change your consent at any time."
      },
      refundPolicy: {
        title: "Refund & Cancellation Policy",
        description: "How cancellations, the statutory right of withdrawal and refunds work for IP9 proxy plans."
      },
      acceptableUse: {
        title: "Acceptable Use Policy",
        description: "Rules for lawful use of the IP9 proxy network, prohibited activities and how to report abuse."
      },
      legalNotice: {
        title: "Legal Notice",
        description: "Company information for IP9: KOKLACS LTD, registered in England and Wales, company number 17449676."
      },
      termsOfService: {
        title: "Terms of Service",
        description: "Terms of service for IP9's proxy services including acceptable use policy, billing terms, and legal compliance requirements."
      },
      blog: {
        title: "Blog",
        description: "Latest insights, tutorials, and industry news about proxy services, web scraping, data collection, and digital infrastructure."
      },
      integrations: {
        title: "Integration Tutorials",
        description: "Step-by-step guides for using IP9 residential and mobile proxies with Python, Node.js, Selenium, Scrapy, Postman, cURL and other tools."
      }
    },

    // Use Case Page
    useCasePage: {
      overview: {
        title1: "Powerful",
        title2: "Use Cases",
        subtitle: "Discover how IP9's proxy infrastructure powers diverse industries with reliable, scalable solutions for every business need.",
        learnMore: "Learn More"
      },
      notFound: {
        title: "Use Case Not Found",
        description: "The use case you're looking for doesn't exist.",
        goBack: "Go Back"
      },
      startFreeTrial: "Start Now",
      contactSales: "Contact Sales",
      keyBenefitsFor: "Key Benefits for",
      whyIP9For: "Why IP9 for",
      features: {
        globalCoverage: {
          title: "Global Coverage",
          description: "Access 300M+ residential proxies across 200+ countries with precise targeting capabilities."
        },
        highSuccessRate: {
          title: "High Success Rate",
          description: "99.9% uptime with optimized residential proxies routing for maximum reliability."
        },
        easyIntegration: {
          title: "Easy Integration",
          description: "Comprehensive APIs and SDKs for seamless residential proxies integration."
        }
      },
      cta: {
        title: "Ready to Get Started with",
        subtitle: "Join thousands of businesses using IP9 for reliable",
        solutions: "solutions"
      },
      viewPricing: "View Pricing",
      defaultBenefitDescription: "Leverage IP9's premium residential proxies infrastructure to achieve this key benefit for your use case.",
      benefitDescriptions: {
        "proxies-for-ai": {
          "Unlimited data collection": "Gather massive datasets from multiple sources using residential proxies without rate limits or IP blocks, enabling comprehensive AI model training.",
          "Bypass AI detection": "Access content that would normally block automated requests using residential proxies, ensuring your AI systems can collect authentic data.",
          "Scale ML training": "Collect diverse training data at enterprise scale with reliable residential proxies infrastructure built for machine learning workflows.",
          "Global data diversity": "Access region-specific content and localized data sources using residential proxies to train more accurate and globally-aware AI models."
        },
        "web-scraping": {
          "Avoid blocks & bans": "Rotate through millions of residential proxies to prevent detection and maintain continuous data extraction without interruptions.",
          "High success rates": "Achieve 99%+ success rates with intelligent residential proxies rotation and retry mechanisms optimized for web scraping.",
          "Rotate IPs automatically": "Seamless residential proxies rotation with customizable intervals and sticky sessions to maintain data consistency.",
          "Parse any website": "Extract data from even the most protected sites with residential proxies that appear as real user traffic."
        },
        "website-testing": {
          "Multi-location testing": "Test your applications using residential proxies from 200+ countries and 1000+ cities to ensure global functionality and performance.",
          "Performance monitoring": "Monitor website performance and load times using residential proxies from different geographic locations and network conditions.",
          "Geo-blocking detection": "Identify and test geo-restrictions using residential proxies to ensure your content is properly accessible or blocked as intended.",
          "User experience validation": "Validate localized content, currency displays, and region-specific features using residential proxies from actual user locations."
        },
        "travel-fare-aggregation": {
          "Real-time pricing": "Access live pricing data from airlines and booking platforms using residential proxies with low-latency connections for accurate fare comparison.",
          "Avoid price discrimination": "Prevent dynamic pricing bias by accessing travel sites through residential proxies from different locations and user profiles.",
          "Multiple platforms": "Simultaneously collect data from hundreds of travel booking sites using residential proxies without triggering anti-bot measures.",
          "Competitive analysis": "Monitor competitor pricing strategies and market trends using residential proxies across different geographical markets."
        },
        "market-research": {
          "Competitor monitoring": "Track competitor pricing, inventory, and marketing strategies using residential proxies across multiple markets and platforms.",
          "Price tracking": "Monitor price changes in real-time using residential proxies across e-commerce platforms to identify trends and opportunities.",
          "Inventory checking": "Verify product availability and stock levels using residential proxies across different regions and market channels.",
          "Market analysis": "Gather comprehensive market intelligence including reviews, ratings, and consumer sentiment data using residential proxies."
        },
        "google-proxies": {
          "Google-optimized": "Specialized residential proxies infrastructure optimized specifically for Google services with high success rates and minimal blocks.",
          "SERP data collection": "Extract search engine results pages accurately using residential proxies that maintain search result integrity and ranking data.",
          "Ad verification": "Verify ad placements and monitor competitor advertising strategies using residential proxies across different geographic markets.",
          "SEO monitoring": "Track keyword rankings, search visibility, and SEO performance using residential proxies from multiple locations and devices."
        },
        "access-restricted-content": {
          "Bypass geo-blocks": "Access region-locked content and services using residential proxies from the specific countries you need.",
          "Content verification": "Verify that your content appears correctly in different markets using residential proxies and test regional compliance requirements.",
          "Global access": "Provide your applications with global reach by accessing content and services using residential proxies from any geographic location.",
          "Streaming support": "Access streaming platforms and media content using residential proxies for research, testing, or content verification purposes."
        },
        "privacy-advantages": {
          "Complete anonymity": "Browse the internet with complete privacy using residential proxies that are indistinguishable from regular users.",
          "Identity protection": "Protect sensitive business intelligence operations and research activities using residential proxies to prevent being traced back to your organization.",
          "Secure browsing": "Maintain security and privacy while accessing potentially risky or monitored websites and platforms using residential proxies.",
          "Business intelligence": "Conduct competitive research and market intelligence gathering using residential proxies without revealing your corporate identity."
        }
      },
      useCases: {
        "proxies-for-ai": {
          title: "Proxies for AI",
          description: "Scale AI training and data collection with reliable residential proxies",
          longDescription: "Power your AI and machine learning projects with our enterprise-grade residential proxies infrastructure. Access diverse data sources without geographical restrictions or rate limiting using residential IP addresses that appear as real users.",
          benefits: ["Unlimited data collection", "Bypass AI detection", "Scale ML training", "Global data diversity"]
        },
        "web-scraping": {
          title: "Web Scraping",
          description: "Extract data at scale with rotating residential proxies",
          longDescription: "Extract data from any website with our advanced residential proxies rotation. Avoid blocks and CAPTCHAs with our pool of residential IP addresses that appear as legitimate user traffic.",
          benefits: ["Avoid blocks & bans", "High success rates", "Rotate IPs automatically", "Parse any website"]
        },
        "website-testing": {
          title: "Website Testing",
          description: "Test websites and applications using residential proxies from different geographic locations",
          longDescription: "Ensure your websites and applications work perfectly from any location using residential proxies. Test geo-restricted content and localized experiences with real residential IP addresses from 200+ countries.",
          benefits: ["Multi-location testing", "Performance monitoring", "Geo-blocking detection", "User experience validation"]
        },
        "travel-fare-aggregation": {
          title: "Travel Fare Aggregation",
          description: "Compare prices and availability across travel booking platforms with residential proxies",
          longDescription: "Access real-time travel pricing data from airlines, hotels, and booking platforms using residential proxies. Provide accurate price comparisons to your customers by accessing sites through residential IP addresses that prevent price discrimination.",
          benefits: ["Real-time pricing", "Avoid price discrimination", "Multiple platforms", "Competitive analysis"]
        },
        "market-research": {
          title: "Market Research",
          description: "Gather market intelligence and competitor data at scale with residential proxies",
          longDescription: "Collect comprehensive market data and competitor intelligence using residential proxies. Monitor pricing, inventory, and market trends across global markets with residential IP addresses that ensure accurate, unbiased data collection.",
          benefits: ["Competitor monitoring", "Price tracking", "Inventory checking", "Market analysis"]
        },
        "google-proxies": {
          title: "Google Proxies",
          description: "Specialized residential proxies optimized for Google services and SERP data",
          longDescription: "Access Google Search, Ads, Shopping, and other Google services without restrictions using our specialized residential proxies. Optimized for SERP scraping and SEO monitoring with residential IP addresses that maintain high success rates.",
          benefits: ["Google-optimized", "SERP data collection", "Ad verification", "SEO monitoring"]
        },
        "access-restricted-content": {
          title: "Access Restricted Content",
          description: "Bypass geo-restrictions and access content from anywhere with residential proxies",
          longDescription: "Access geo-restricted content, streaming platforms, and region-locked websites using residential proxies. Perfect for content verification and global market research with residential IP addresses from specific countries you need.",
          benefits: ["Bypass geo-blocks", "Content verification", "Global access", "Streaming support"]
        },
        "privacy-advantages": {
          title: "Privacy Advantages",
          description: "Protect your identity and maintain anonymity online with residential proxies",
          longDescription: "Maintain complete privacy and anonymity while browsing using residential proxies. Protect sensitive research and business intelligence gathering operations with residential IP addresses that are indistinguishable from regular users.",
          benefits: ["Complete anonymity", "Identity protection", "Secure browsing", "Business intelligence"]
        }
      }
    },

    // Pricing Page
    pricingPage: {
      mostPopular: "MOST POPULAR",
      from: "From",
      month: "month",
      save: "Save",
      contactSales: "Contact Sales",
      getStarted: "Get Started",
      residentialProxy: "Residential Proxy",
      mobileProxy: "Mobile Proxy",
      simpleTransparent: "Simple, Transparent",
      pricing: "Pricing",
      residentialSubtitle: "Scale with confidence using our premium residential proxy network. Volume discounts up to 31% off.",
      mobileSubtitle: "Access real mobile IPs with carrier rotation. Perfect for mobile app testing and social media automation.",
      generalSubtitle: "Choose the perfect plan for your needs. No hidden fees, no setup costs. Volume discounts available.",
      uptimeSLA: "99.9% Uptime SLA",
      happyCustomers: "10,000+ Happy Customers",
      instantActivation: "Instant Activation",
      pricingCalculator: "Pricing Calculator",
      calculatorSubtitle: "Calculate your estimated monthly costs based on usage",
      faq: {
        title: "Frequently Asked Questions",
        subtitle: "Everything you need to know about our pricing",
        questions: [
          {
            question: "How does volume pricing work?",
            answer: "Our residential proxy pricing decreases with volume: $1.30/GB for 1-30GB, $1.10/GB for 30-100GB, $1.00/GB for 100GB-1TB, and $0.90/GB for 1-5TB. Enterprise clients get custom rates for 5TB+."
          },
          {
            question: "Do you offer free trials?",
            answer: "Yes! We offer a 24-hour mobile proxy trial for $10/proxy. For residential proxies, contact our sales team for a custom trial based on your needs."
          },
          {
            question: "What payment methods do you accept?",
            answer: "We accept all major credit cards, PayPal, wire transfers, and cryptocurrency. Enterprise clients can also pay via invoice with NET30 terms."
          },
          {
            question: "Can I change plans anytime?",
            answer: "Absolutely! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any billing differences."
          },
          {
            question: "What's included in Enterprise plans?",
            answer: "Enterprise plans include volume discounts up to 50%, dedicated account management, SLA guarantees, custom contracts, compliance support, and priority technical support."
          }
        ]
      },
      cta: {
        title: "Ready to Get Started?",
        subtitle: "Join thousands of businesses using IP9 for reliable proxy infrastructure. Start with our most popular plan or contact sales for custom solutions.",
        startPlan: "Start now",
        contactSales: "Contact Sales Team",
        noSetupFees: "No setup fees",
        cancelAnytime: "Cancel anytime",
        support247: "24/7 support"
      }
    },

    // About Page
    aboutPage: {
      title: "About IP9",
      heroDescription: "We're building the world's most reliable proxy infrastructure to power the next generation of data-driven businesses.",
      stats: {
        ipAddresses: "IP Addresses",
        countries: "Countries",
        happyCustomers: "Happy Customers",
        uptimeSLA: "Uptime SLA"
      },
      story: {
        title: "Our Story",
        subtitle: "Founded with a mission to democratize access to reliable proxy infrastructure",
        paragraph1: "IP9 was founded in 2020 by a team of infrastructure engineers who experienced firsthand the challenges of building reliable data collection systems at scale. Frustrated by existing proxy providers' limitations, we set out to build something better.",
        paragraph2: "Today, we operate the world's largest residential proxy network with over 300 million IP addresses across 200+ countries. Our infrastructure powers everything from AI training and market research to e-commerce monitoring and travel aggregation.",
        paragraph3: "We're proud to serve over 10,000 businesses worldwide, from startups to Fortune 500 companies, helping them collect data, test applications, and expand globally with confidence and reliability."
      },
      values: {
        title: "Our Values",
        subtitle: "The principles that guide everything we do at IP9",
        securityFirst: {
          title: "Security First",
          description: "We prioritize security and privacy in everything we do, with SOC2 compliance and enterprise-grade infrastructure."
        },
        globalReach: {
          title: "Global Reach",
          description: "Our worldwide network ensures reliable access from any location with comprehensive coverage and targeting options."
        },
        customerSuccess: {
          title: "Customer Success",
          description: "Dedicated support teams and account managers ensure your success with 24/7 technical assistance."
        },
        innovation: {
          title: "Innovation",
          description: "Continuous innovation in proxy technology and infrastructure to stay ahead of industry demands."
        }
      },
      cta: {
        title: "Ready to Work with Us?",
        subtitle: "Join thousands of businesses that trust IP9 for their proxy infrastructure needs.",
        startFreeTrial: "Start Now",
        contactSales: "Contact Sales"
      }
    },

    // Common
    common: {
      learnMore: "Learn More",
      getStarted: "Get Started",
      readMore: "Read More",
      backToHome: "Back to Home",
      loading: "Loading...",
      error: "Something went wrong",
      tryAgain: "Try Again"
    },

    // Documentation
    documentation: {
      searchPlaceholder: "Search documentation...",
      menuToggle: "Documentation Menu",
      sections: {
        residentialProxies: {
          title: "Residential Proxies",
          description: "Complete guide to using IP9 residential proxies for web scraping, data collection, and automation.",
          gettingStarted: "Getting Started",
          makingRequests: "Making Requests",
          locationSettings: "Location Settings",
          sessionControl: "Session Control",
          protocols: "Protocols",
          restrictedTargets: "Restricted Targets"
        },
        locationSettings: {
          country: "Country",
          city: "City", 
          state: "State",
          continent: "Continent",
          zipPostalCode: "ZIP/Postal Code",
          coordinates: "Coordinates",
          asnTargeting: "ASN Targeting"
        }
      }
    },

    // Locations Section
    locations: {
      title: "Available Locations",
      subtitle: "Access mobile and residential proxies from 52 countries and 1000+ cities worldwide with premium carrier networks.",
      countriesAvailable: "Countries Available",
      citiesCovered: "Cities Covered",
      mobileCarriers: "Mobile Carriers",
      supportedCountries: "Supported Countries",
      showLessCountries: "Show Less Countries",
      showAllCountries: "Show All Countries",
      premiumMobileCarriers: "Premium Mobile Carriers",
      moreCarriersText: "And many more premium carriers worldwide, ensuring maximum diversity and reliability.",
      globalCoverage: "Global Coverage"
    }
  },

  zh: {
    // Header - 头部导航
    header: {
      products: "首页",
      pricing: "价格", 
      useCases: "应用场景",
      docs: "文档",
      documentation: "文档",
      blog: "博客",
      about: "关于我们",
      careers: "招聘",
      getStarted: "开始使用",
      chinese: "中文",
      login: "登录",
      dashboard: "控制台",
      dropdown: {
        useCases: {
          proxiesForAI: "AI代理",
          webScraping: "网页抓取",
          websiteTesting: "网站测试",
          travelFareAggregation: "旅行价格聚合",
          marketResearch: "市场研究",
          googleProxies: "谷歌代理",
          accessRestrictedContent: "访问受限内容",
          privacyAdvantages: "隐私优势"
        },
        pricing: {
          residentialProxies: "住宅代理",
          mobileProxies: "移动代理"
        }
      }
    },
    
    // Hero Section - 首页主要内容
    hero: {
      title1: "企业级移动与",
      title2: "住宅代理服务",
      subtitle: "访问遍布200多个国家的3亿+IP地址，99.9%正常运行时间保证。完美适用于网页抓取、数据收集和大规模自动化。",
      startTrial: "立即开始",
      viewPricing: "查看价格",
      customers: "全球10,000+客户",
      countries: "200+国家与1000+城市",
      uptime: "99.9%正常运行保证",
      ipsWorldwide: "全球3亿+IP地址",
      responseTime: "<500毫秒响应时间",
      enterpriseSecurity: "企业级安全",
      soc2Monitoring: "SOC2合规与24/7监控",
      simpleTransparentPricing: "简单透明的定价"
    },

    // Stats Section - 统计数据
    stats: {
      ipAddresses: "IP地址",
      countries: "国家", 
      uptime: "正常运行时间",
      support: "支持"
    },

    // Proxies Pages - 代理页面
    proxies: {
      main: {
        hero: {
          title1: "优质全球",
          title2: "代理网络",
          subtitle: "访问全球最可靠的住宅和移动代理网络。3亿+道德来源IP，具备企业级性能。"
        }
      },
      residential: {
        hero: {
          title1: "住宅代理",
          title2: "支持IP轮换",
          subtitle: "专为各类商业项目设计的高性价比代理解决方案。道德、快速的住宅代理，旨在最大限度减少验证码和封锁——且预算友好。"
        },
        standOut: {
          title: "IP9住宅代理为何与众不同",
          content: [
            "IP9提供高质量的住宅代理，拥有极高的成功率和具有竞争力的价格。我们道德来源的住宅IP覆盖190+个国家，每个IP都关联真实的家庭连接和合法的互联网服务提供商。隐私、稳定性和性能是我们网络的核心。",
            "我们的轮换住宅代理包含中小企业及个人用户所需的所有核心功能。无论是访问受地理限制的内容、运行大规模网页抓取，还是验证数字广告，IP9都能提供可靠且实惠的住宅代理解决方案。",
            "凭借全球网络、不过期流量和灵活的定价，IP9让每个人都能享受到优质的住宅IP服务。"
          ]
        },
        features: {
          title: "核心功能",
          subtitle: "成功进行数据采集和自动化所需的一切。",
          list: [
            { title: "高级粘性会话", description: "自定义会话时长，保持同一IP 1秒至7天，确保在线身份的一致性和稳定性。" },
            { title: "流量不过期", description: "享受按量付费模式，流量永不过期——无需每月续费或担心使用压力。" },
            { title: "无缝兼容", description: "支持HTTP、HTTPS和SOCKS5协议，确保IP9代理在所有工作流中顺畅运行，兼容任何软件。" },
            { title: "自定义IP轮换", description: "每次请求轮换IP，或在1秒至7天内保持同一住宅IP。" },
            { title: "精准地理定位", description: "覆盖190+个国家，并可免费精确到特定的州或城市——非常适合本地化内容访问和研究。" }
          ]
        },
        whyChoose: {
          title: "为什么选择IP9？",
          content: [
            "如果您需要全球住宅代理来进行日常在线操作，IP9是可靠的选择。我们在全球几乎每个国家都提供真实的住宅IP地址，所有来源均符合道德和法律规范，同时保持极具竞争力的价格。",
            "通过我们要灵活的按量付费计划，您可以购买所需的流量，并随时使用。凭借不过期流量、批量折扣和预算友好的费率，IP9专为满足您的扩展需求而设计。",
            "从持久的粘性会话和双重认证到全协议支持（HTTP/HTTPS/SOCKS5），IP9住宅代理专为处理各种用例而构建——包括网页抓取、市场研究、广告验证、SEO任务和绕过地理限制。"
          ]
        },
        advantages: {
          title: "更多优势",
          list: [
            { title: "最具性价比的代理服务器", description: "无需支付高昂溢价，即可使用具备高级功能的企业级住宅代理。" },
            { title: "24/7专家支持", description: "有问题或遇到困难？我们的支持团队全天候待命，随时为您提供帮助。" }
          ]
        }
      },
      mobile: {
        hero: {
          title1: "高性能",
          title2: "移动代理",
          subtitle: "来自全球主要运营商的真实4G/5G移动连接。完美适用于账户管理、社交媒体和应用测试。"
        },
        features: {
          title: "移动4G/5G动力",
          subtitle: "利用移动运营商网络的信任度进行最敏感的操作。",
          list: [
            { title: "真实运营商IP", description: "通过来自Verizon、AT&T、Vodafone等运营商的真实SIM卡连接。" },
            { title: "自动轮换", description: "按设定间隔自动轮换IP，或通过API按需轮换。" },
            { title: "最高信任评分", description: "移动IP拥有最高的信任评分，使其免受大多数封锁。" },
            { title: "无限带宽", description: "在我们专用的移动端口上享受不限流量的带宽。" }
          ]
        }
      }
    },

    // Features Section - 功能特色
    features: {
      title: "为什么选择",
      subtitle: "专为规模、可靠性和性能设计的企业级代理基础设施。受到全球领先企业信赖。",
      features: [
        {
          title: "全球IP网络",
          description: "访问来自全球200多个国家和1000多个城市的超过3亿个IP地址。"
        },
        {
          title: "99.9%正常运行时间",
          description: "企业级基础设施确保您关键业务的最大可靠性。"
        },
        {
          title: "闪电般快速",
          description: "高速连接，亚秒级响应时间，确保最佳性能。"
        },
        {
          title: "高级定位",
          description: "按国家、城市、州、邮政编码、ASN甚至特定坐标进行定位。"
        },
        {
          title: "会话控制",
          description: "保持粘性会话长达24小时，具有完整的会话管理控制。"
        },
        {
          title: "24/7支持",
          description: "专家支持团队全天候为您提供集成和问题解决帮助。"
        }
      ]
    },

    // Use Cases Section - 应用场景
    useCases: {
      title1: "强大的",
      title2: "应用场景",
      subtitle: "从AI训练到市场研究，我们的代理解决方案为各个行业提供可靠、可扩展的基础设施。",
      exploreAll: "查看所有应用场景",
      cases: [
        {
          title: "网页抓取",
          description: "从任何网站提取数据，无需担心被阻止或速率限制。"
        },
        {
          title: "市场研究", 
          description: "从全球市场收集竞争情报和定价数据。"
        },
        {
          title: "广告验证",
          description: "验证广告投放并监控不同地区的广告活动。"
        },
        {
          title: "价格监控",
          description: "实时跟踪价格变化和竞争对手策略。"
        },
        {
          title: "社交媒体",
          description: "安全地管理多个账户并自动化社交媒体操作。"
        },
        {
          title: "SEO监控",
          description: "全球跟踪搜索排名并监控SEO性能。"
        }
      ]
    },

    // Pricing Preview Section - 价格预览
    pricingPreview: {
      title1: "简单透明的",
      title2: "定价方案",
      subtitle: "使用我们的企业级基础设施放心扩展业务。批量折扣高达31%。",
      noSetupFees: "无设置费用",
      instantActivation: "即时激活",
      support247: "24/7支持",
      mostPopular: "最受欢迎",
      residentialProxies: "住宅代理",
      residentialSubtitle: "全球覆盖的优质住宅IP",
      unlimitedMobile: "无限移动代理",
      mobileSubtitle: "真实移动IP与运营商轮换",
      unlimitedMobileProxies: "无限移动代理",
      trueMobileIPs: "真实移动IP与运营商轮换",
      professional: "专业版",
      perGB: "每GB",
      save: "节省",
      starter: "入门版",
      business: "商业版", 
      scale: "规模版",
      trial: "试用版",
      monthly: "月付版",
      enterprise: "企业版",
      planNames: {
        starter: "入门版",
        professional: "专业版", 
        business: "商业版",
        scale: "规模版",
        enterprise: "企业版",
        trial: "试用版",
        monthly: "月付版"
      },
      perProxy24h: "每代理/24小时",
      perProxyMonth: "每代理/月",
      contactSales: "联系销售",
      viewAllPlans: "查看所有方案",
      getTrial: "获取试用",
      viewAllMobilePlans: "查看所有移动代理方案",
      volumePricingDiscounts: "批量定价折扣",
      off: "折扣",
      scaleWithConfidence: "安心扩展业务。使用越多，节省越多。",
      viewAllPricing: "查看所有价格",
      contactSales: "联系销售",
      features: {
        unlimitedSessions: "无限并发会话",
        freeGeoTargeting: "免费地理定位",
        support247: "24/7 支持",
        noHiddenFees: "无隐藏费用",
        ipQualityEngine: "IP质量保证引擎",
        responseTime: "0.5秒响应时间"
      },
      countriesAvailable: "52+ 国家可用",
      accessProxiesWorldwide: "访问全球主要国家的代理，支持城市级定位",
      viewAllLocations: "查看所有位置",
      cities: "城市",
      countries: {
        "United States": "美国",
        "United Kingdom": "英国", 
        "Germany": "德国",
        "France": "法国",
        "Canada": "加拿大",
        "Australia": "澳大利亚",
        "Japan": "日本",
        "Brazil": "巴西"
      },
      volumeRange1: "1-30GB",
      volumeRange2: "30GB - 100GB", 
      volumeRange3: "100GB-1TB",
      volumeRange4: "1-5TB",
      mobilePlans: {
        trial: {
          name: "试用版",
          volume: "24小时",
          unit: "每代理/24小时"
        },
        monthly: {
          name: "月付版",
          volume: "30天",
          unit: "每代理/月"
        },
        enterprise: {
          name: "企业版",
          volume: "批量定价",
          unit: "联系销售"
        }
      },
      custom: "定制",
      upTo40: "高达40%",
      planFeatures: {
        starter: {
          ips: "3亿+住宅IP",
          countryTargeting: "国家定位",
          cityTargeting: "城市定位",
          ispTargeting: "ISP定位",
          httpSupport: "HTTP/HTTPS支持",
          support247: "24/7支持",
          uptime: "99.9%正常运行时间"
        },
        professional: {
          allStarterFeatures: "包含入门版所有功能",
          cityTargeting: "城市定位",
          ispTargeting: "ISP定位",
          prioritySupport: "优先支持",
          dedicatedPools: "专用IP池",
          apiAccess: "API访问"
        },
        business: {
          allProfessionalFeatures: "包含专业版所有功能",
          customIntegrations: "定制集成",
          accountManager: "客户经理",
          slaGuarantee: "SLA保证",
          privatePools: "私有IP池",
          advancedAnalytics: "高级分析"
        },
        scale: {
          allBusinessFeatures: "包含商业版所有功能",
          priorityRouting: "优先路由",
          customEndpoints: "定制端点",
          whiteLabelOptions: "白标选项",
          dedicatedInfrastructure: "专用基础设施",
          phoneSupport: "24/7电话支持"
        },
        enterprise: {
          allScaleFeatures: "包含规模版所有功能",
          volumeDiscounts: "批量折扣",
          customContracts: "定制合同",
          onPremiseOptions: "本地部署选项",
          complianceSupport: "合规支持",
          trainingOnboarding: "培训与入职"
        }
      },
      mobilePlanFeatures: {
        trial: {
          realMobileIPs: "真实移动IP",
          carrierRotation: "运营商轮换",
          networks4G5G: "4G/5G网络",
          basicSupport: "基础支持",
          countriesAvailable: "52+国家可用"
        },
        monthly: {
          allTrialFeatures: "包含试用版所有功能",
          extendedUsage: "延长使用时间",
          prioritySupport: "优先支持",
          customRotation: "定制轮换",
          globalLocations: "52+全球位置",
          stickySessions: "粘性会话"
        },
        enterprise: {
          dedicatedMobilePools: "专用移动IP池",
          slaGuarantee: "SLA保证",
          support247: "24/7支持",
          customSolutions: "定制解决方案",
          privateAPN: "私有APN",
          bulkDiscounts: "批量折扣"
        }
      }
    },

    // Locations Section - 位置信息
    locations: {
      title: "可用位置",
      subtitle: "通过优质运营商网络访问来自52个国家和1000+城市的移动和住宅代理。",
      countriesAvailable: "可用国家",
      citiesCovered: "覆盖城市", 
      mobileCarriers: "移动运营商",
      supportedCountries: "支持的国家",
      showLessCountries: "显示更少国家",
      showAllCountries: "显示所有国家",
      premiumMobileCarriers: "优质移动运营商",
      moreCarriersText: "以及全球更多优质运营商，确保最大的多样性和可靠性。",
      globalCoverage: "全球覆盖",
      countries: {
        "United States": "美国",
        "United Kingdom": "英国",
        "Germany": "德国",
        "France": "法国",
        "Canada": "加拿大",
        "Australia": "澳大利亚",
        "Japan": "日本",
        "South Korea": "韩国",
        "Brazil": "巴西",
        "Mexico": "墨西哥",
        "Spain": "西班牙",
        "Italy": "意大利",
        "Netherlands": "荷兰",
        "Sweden": "瑞典",
        "Norway": "挪威",
        "Denmark": "丹麦",
        "Finland": "芬兰",
        "Poland": "波兰",
        "Czech Republic": "捷克共和国",
        "Austria": "奥地利",
        "Switzerland": "瑞士",
        "Belgium": "比利时",
        "Ireland": "爱尔兰",
        "Portugal": "葡萄牙",
        "India": "印度",
        "Singapore": "新加坡",
        "Hong Kong": "香港",
        "Taiwan": "台湾",
        "Thailand": "泰国",
        "Malaysia": "马来西亚",
        "Philippines": "菲律宾",
        "Indonesia": "印度尼西亚",
        "Vietnam": "越南",
        "Turkey": "土耳其",
        "Israel": "以色列",
        "South Africa": "南非",
        "Argentina": "阿根廷",
        "Chile": "智利",
        "Colombia": "哥伦比亚",
        "Peru": "秘鲁",
        "Russia": "俄罗斯",
        "Ukraine": "乌克兰",
        "Romania": "罗马尼亚",
        "Bulgaria": "保加利亚",
        "Hungary": "匈牙利",
        "Croatia": "克罗地亚",
        "Serbia": "塞尔维亚",
        "Slovenia": "斯洛文尼亚",
        "Slovakia": "斯洛伐克",
        "Lithuania": "立陶宛",
        "Latvia": "拉脱维亚",
        "Estonia": "爱沙尼亚",
        "Greece": "希腊",
        "Cyprus": "塞浦路斯"
      }
    },

    // Integrations Section - 开发者集成
    integrations: {
      title1: "开发者",
      title2: "集成指南", 
      subtitle: "为流行工具和框架提供分步集成指南。通过我们全面的教程，几分钟内即可启动代理设置。",
      tutorials: "教程",
      categories: "类别",
      updated: "更新时间",
      readGuide: "阅读指南",
      viewAllIntegrations: "查看所有集成",
      readyInMinutes: "快速上手",
      popular: "热门",
      browseByCategory: "按类别浏览",
      ctaTitle: "准备好集成IP9代理了吗？",
      ctaSubtitle: "探索我们完整的集成教程集合，让您的代理在几分钟内与您喜爱的工具配合工作。",
      browseAllTutorials: "浏览所有教程",
      viewPricing: "查看定价",
      readTime: "分钟阅读",
      articles: [
        {
          title: "如何在Python Requests库中使用IP9代理",
          excerpt: "完整指南：将IP9住宅和移动代理集成到Python requests中，用于网页抓取和API调用。"
        },
        {
          title: "Selenium WebDriver与IP9代理：完整设置指南",
          excerpt: "学习如何配置Selenium WebDriver与IP9代理，用于自动化浏览器测试和网页抓取。"
        },
        {
          title: "使用IP9代理轮换构建Scrapy爬虫",
          excerpt: "掌握使用IP9代理轮换进行大规模网页抓取项目的Scrapy爬虫开发。"
        },
        {
          title: "Node.js Axios HTTP客户端与IP9代理",
          excerpt: "完整指南：使用IP9代理和Node.js Axios进行API调用和网页抓取。"
        },
        {
          title: "配置Postman与IP9代理进行API测试",
          excerpt: "分步指南：在Postman中设置IP9代理，用于安全的API测试和开发。"
        },
        {
          title: "掌握cURL与IP9代理：基本命令",
          excerpt: "在命令行HTTP操作中使用IP9代理的基本cURL命令和技术。"
        },
        {
          title: "Beautiful Soup网页抓取与IP9代理集成",
          excerpt: "使用Beautiful Soup和IP9代理进行可靠数据提取的综合指南。"
        },
        {
          title: "构建支持IP9代理的Chrome扩展",
          excerpt: "创建利用IP9代理增强功能和隐私保护的强大Chrome扩展。"
        },
        {
          title: "使用Insomnia和IP9代理进行API测试",
          excerpt: "使用Insomnia REST客户端和IP9代理集成进行全面测试的高级API测试技术。"
        }
      ]
    },

    // Testimonials Section - 客户评价
    testimonials: {
      title: "受到行业领导者信赖",
      subtitle: "加入使用IP9代理为其数据操作提供动力的数千家公司。",
      at: "在",
      testimonials: [
        {
          name: "陈莎拉",
          role: "数据工程主管",
          company: "科技公司",
          content: "IP9的移动代理对我们的数据收集来说是革命性的。可靠性和速度无与伦比。",
          rating: 5
        },
        {
          name: "麦克·罗德里格斯", 
          role: "首席技术官",
          company: "数据流公司",
          content: "我们尝试过许多代理提供商，但IP9的客户支持和正常运行时间保证使他们脱颖而出。",
          rating: 5
        },
        {
          name: "王丽莎",
          role: "工程副总裁", 
          company: "规模数据",
          content: "地理定位功能正是我们全球市场研究所需要的。",
          rating: 5
        }
      ]
    },

    // FAQ Section - 常见问题
    faq: {
      title: "常见问题",
      subtitle: "关于IP9代理您需要了解的一切。",
      stillHaveQuestions: "仍有疑问？我们的支持团队随时为您提供帮助。",
      contactSupport: "联系支持",
      scheduleCall: "安排通话",
      faqs: [
        {
          question: "住宅代理和移动代理有什么区别？",
          answer: "住宅代理使用来自家庭互联网连接的IP地址，而移动代理使用来自移动运营商的IP。移动代理非常适合社交媒体和移动应用测试，为移动特定用例提供更高的成功率。"
        },
        {
          question: "你们提供免费试用吗？",
          answer: "是的！我们为移动代理提供10美元试用（24小时访问）和住宅代理试用。请联系我们的支持团队support@ip9.io开始使用。"
        },
        {
          question: "你们支持哪些地区？",
          answer: "我们支持全球200多个国家和1000多个城市，包括美国、英国、德国、日本、澳大利亚等主要市场。您可以按国家、城市、州甚至特定邮政编码进行定位。"
        },
        {
          question: "你们的代理有多可靠？",
          answer: "我们通过企业级基础设施保证99.9%的正常运行时间。我们的代理网络持续监控和优化，以确保最大的可靠性和性能。"
        },
        {
          question: "你们的定价模式是什么？",
          answer: "我们提供基于使用量的灵活定价。住宅代理起价1.3美元/GB，有批量折扣。移动代理24小时试用起价10美元。请联系我们获取企业定价。"
        },
        {
          question: "你们提供客户支持吗？",
          answer: "是的，我们通过电子邮件、在线聊天为您提供24/7客户支持，并为企业客户提供专门的客户经理。我们的团队在这里帮助您进行设置、集成和解决任何问题。"
        }
      ]
    },

    // CTA Section - 行动号召
    cta: {
      title: "准备好扩展您的业务了吗？",
      subtitle: "加入10,000+使用IP9进行可靠、高性能代理基础设施的企业。今天就开始免费试用，体验不同之处。",
      startTrial: "立即开始",
      contactSales: "联系销售",
      soc2Compliant: "SOC2合规",
      uptimeSLA: "99.9%正常运行时间SLA",
      support247: "24/7支持",
      enterpriseSolutions: "企业解决方案",
      enterpriseDescription: "为大规模运营定制代理基础设施，提供专属支持。",
      talkToSales: "联系销售",
      developerResources: "开发者资源", 
      developerDescription: "全面的API文档、SDK和集成指南。",
      viewDocs: "查看文档"
    },

    // Footer - 页脚
    footer: {
      description: "用于大规模可靠数据收集的企业级移动和住宅代理。",
      products: {
        title: "产品",
        items: [
          "住宅代理",
          "移动代理", 
          "API访问"
        ]
      },
      useCases: {
        title: "应用场景",
        items: [
          "网页抓取",
          "市场研究",
          "广告验证", 
          "价格监控",
          "社交媒体",
          "SEO工具"
        ]
      },
      company: {
        title: "公司", 
        items: [
          "关于我们",
          "招聘",
          "隐私政策",
          "服务条款"
        ]
      },
      support: {
        title: "支持",
        items: [
          "联系我们"
        ]
      },
      contact: {
        email: "邮箱：support@ip9.io",
        address: "128 City Road, London, EC1V 2NX, United Kingdom"
      },
      copyright: "© 2026 KOKLACS LTD. 保留所有权利。",
      rightsReserved: "保留所有权利。",
      cookieSettings: "Cookie 设置",
      guides: "指南",
      weAccept: "支付方式",
      blog: "博客",
      legal: {
        title: "法律",
        legalNotice: "法律声明",
        terms: "服务条款",
        privacy: "隐私政策",
        cookies: "Cookie 政策",
        refund: "退款政策",
        aup: "可接受使用政策"
      },
      companyLine: {
        tradingNameOf: "是以下公司的商号：",
        registeredIn: "一家在英格兰和威尔士注册的公司",
        companyNo: "公司编号",
        registeredOffice: "注册地址"
      },
      followUs: "关注我们",
      privacyPolicy: "隐私政策",
      termsOfService: "服务条款"
    },



    // Contact Page - 联系我们页面

    cookies: {
      title: "Cookies",
      text: "我们仅使用运行本网站所必需的存储。经您同意后，我们还会使用分析类 Cookie（Google Analytics、PostHog）来了解网站的使用情况，以及营销类 Cookie（Meta Pixel）来衡量我们在 Facebook 和 Instagram 上的广告效果。您可以随时更改您的选择。",
      policyLink: "Cookie 政策",
      accept: "全部接受",
      reject: "全部拒绝",
      customise: "自定义选择",
      save: "保存我的选择",
      analyticsLabel: "分析",
      analyticsText: "Google Analytics 和 PostHog：汇总的使用统计数据以及会话回放（表单输入已遮盖）。",
      marketingLabel: "营销",
      marketingText: "Meta Pixel：衡量来自我们 Facebook 和 Instagram 广告的访问，并让我们在 Meta 上展示相关广告。"
    },
    contact: {
      title: "联系我们",
      subtitle: "联系我们的团队",
      description: "我们致力于帮助您成功使用IP9的代理解决方案。通过以下任何渠道联系我们，我们将尽快回复您。",
      methods: {
        title: "我们如何为您提供帮助？",
        subtitle: "根据您的需求选择最佳联系方式",
        support: {
          title: "技术支持",
          subtitle: "获得集成和故障排除帮助",
          description: "我们的技术团队随时准备协助API集成、配置问题和故障排除。非常适合开发者和技术用户。",
          responseTime: "2-4小时内回复"
        },
        sales: {
          title: "销售与企业",
          subtitle: "讨论定价和企业解决方案",
          description: "联系我们的销售团队获取定制定价、企业功能、专属支持以及为您的业务需求量身定制的批量折扣。",
          responseTime: "1-2小时内回复"
        },
        telegram: {
          title: "Telegram支持",
          subtitle: "紧急事务快速聊天",
          description: "通过Telegram获得即时协助，解决紧急问题、快速咨询，并获得我们团队的实时支持。",
          responseTime: "30分钟内回复"
        }
      },
      businessHours: {
        title: "营业时间",
        weekdays: "周一至周五",
        weekends: "周六至周日",
        note: "营业时间外的紧急事务，请使用Telegram或发送邮件至urgent@ip9.io"
      },
      location: {
        title: "我们的位置",
        headquarters: "总部",
        timezone: "时区",
        coverage: "全球覆盖",
        coverageDescription: "我们的公司注册于英国伦敦，提供24/7支持和全球代理基础设施。"
      },
      supportCategories: {
        title: "我们能为您做什么",
        subtitle: "我们的支持团队涵盖您代理需求的各个方面",
        technical: {
          title: "技术支持",
          items: [
            "API集成协助",
            "配置故障排除",
            "性能优化",
            "身份验证设置",
            "定制实施"
          ]
        },
        billing: {
          title: "计费与账户",
          items: [
            "付款和计费问题",
            "套餐升级和变更",
            "使用监控",
            "发票和收据请求",
            "账户管理"
          ]
        },
        sales: {
          title: "销售与企业",
          items: [
            "定制价格报价",
            "企业功能",
            "批量折扣",
            "专属支持",
            "SLA协议"
          ]
        }
      },
      emergency: {
        title: "紧急联系",
        description: "对于影响您业务运营的关键服务问题，请立即联系我们："
      }
    },

    // SEO Meta Data - SEO元数据
    seo: {
      siteTitle: "IP9",
      home: {
        title: "首页",
        description: "企业级移动和住宅代理服务，覆盖200+国家的3亿+IP地址。99.9%正常运行时间，高速稳定的数据收集解决方案。"
      },
      pricing: {
        title: "价格",
        description: "移动和住宅代理的透明定价。灵活套餐起价1.30美元/GB，提供批量折扣和企业解决方案。"
      },
      useCases: {
        title: "应用场景",
        description: "了解IP9的代理基础设施如何为全球企业提供网页抓取、市场研究、广告验证、价格监控等强大功能。"
      },
      docs: {
        title: "文档",
        description: "IP9代理服务的完整API文档、集成指南和技术资源。通过我们全面的开发者文档快速入门。"
      },
      about: {
        title: "关于我们",
        description: "了解IP9为全球企业提供可靠、高性能代理基础设施的使命。受到全球10,000+企业信赖。"
      },
      contact: {
        title: "联系我们",
        description: "联系IP9支持团队。技术支持、销售咨询和企业解决方案。多种联系方式，快速响应时间。"
      },
      privacyPolicy: {
        title: "隐私政策",
        description: "KOKLACS LTD（IP9）如何根据英国GDPR和欧盟GDPR收集、使用和保护个人数据，以及您如何行使您的权利。"
      },
      cookiePolicy: {
        title: "Cookie 政策",
        description: "IP9网站使用哪些Cookie及类似技术、原因，以及如何随时更改您的同意。"
      },
      refundPolicy: {
        title: "退款与取消政策",
        description: "IP9代理套餐的取消、法定撤回权和退款规则。"
      },
      acceptableUse: {
        title: "可接受使用政策",
        description: "合法使用IP9代理网络的规则、禁止的活动以及如何举报滥用。"
      },
      legalNotice: {
        title: "法律声明",
        description: "IP9公司信息：KOKLACS LTD，在英格兰和威尔士注册，公司编号17449676。"
      },
      termsOfService: {
        title: "服务条款",
        description: "IP9代理服务的服务条款，包括可接受使用政策、计费条款和法律合规要求。"
      },
      blog: {
        title: "博客",
        description: "关于代理服务、网页抓取、数据收集和数字基础设施的最新见解、教程和行业新闻。"
      },
      integrations: {
        title: "集成教程",
        description: "在 Python、Node.js、Selenium、Scrapy、Postman、cURL 等工具中使用 IP9 住宅和移动代理的分步指南。"
      }
    },

    // Use Case Page - 应用场景页面
    useCasePage: {
      overview: {
        title1: "强大的",
        title2: "应用场景",
        subtitle: "了解IP9的代理基础设施如何为各行各业提供可靠、可扩展的解决方案，满足每个业务需求。",
        learnMore: "了解更多"
      },
      notFound: {
        title: "未找到应用场景",
        description: "您要查找的应用场景不存在。",
        goBack: "返回"
      },
      startFreeTrial: "立即开始",
      contactSales: "联系销售",
      keyBenefitsFor: "关键优势",
      whyIP9For: "为什么选择IP9",
      features: {
        globalCoverage: {
          title: "全球覆盖",
          description: "访问遍布200+国家的3亿+住宅代理，具备精确定位功能。"
        },
        highSuccessRate: {
          title: "高成功率",
          description: "99.9%的正常运行时间，通过优化住宅代理路由确保最大可靠性。"
        },
        easyIntegration: {
          title: "易于集成",
          description: "全面的API和SDK，实现无缝住宅代理集成。"
        }
      },
      cta: {
        title: "准备开始使用",
        subtitle: "加入数千家使用IP9进行可靠",
        solutions: "解决方案的企业"
      },
      viewPricing: "查看价格",
      defaultBenefitDescription: "利用IP9的优质住宅代理基础设施实现您应用场景的关键优势。",
      benefitDescriptions: {
        "proxies-for-ai": {
          "Unlimited data collection": "使用住宅代理从多个来源收集大量数据集，无速率限制或IP封锁，实现全面的AI模型训练。",
          "Bypass AI detection": "使用住宅代理访问通常会阻止自动化请求的内容，确保您的AI系统能够收集真实数据。",
          "Scale ML training": "在企业规模上收集多样化的训练数据，使用为机器学习工作流构建的可靠住宅代理基础设施。",
          "Global data diversity": "使用住宅代理访问特定地区的内容和本地化数据源，训练更准确和全球感知的AI模型。"
        },
        "web-scraping": {
          "Avoid blocks & bans": "通过数百万住宅代理轮换防止检测，维持连续数据提取而无中断。",
          "High success rates": "通过为网页抓取优化的智能住宅代理轮换和重试机制实现99%+的成功率。",
          "Rotate IPs automatically": "无缝住宅代理轮换，具有可定制间隔和粘性会话以保持数据一致性。",
          "Parse any website": "使用看起来像真实用户流量的住宅代理从最受保护的网站提取数据。"
        },
        "website-testing": {
          "Multi-location testing": "使用来自200+国家和1000+城市的住宅代理测试您的应用程序，确保全球功能和性能。",
          "Performance monitoring": "使用来自不同地理位置和网络条件的住宅代理监控网站性能和加载时间。",
          "Geo-blocking detection": "使用住宅代理识别和测试地理限制，确保您的内容按预期正确访问或阻止。",
          "User experience validation": "使用来自实际用户位置的住宅代理验证本地化内容、货币显示和区域特定功能。"
        },
        "travel-fare-aggregation": {
          "Real-time pricing": "使用住宅代理通过低延迟连接从航空公司和预订平台获取实时定价数据，实现准确的票价比较。",
          "Avoid price discrimination": "通过使用住宅代理从不同位置和用户档案访问旅行网站来防止动态定价偏见。",
          "Multiple platforms": "使用住宅代理同时从数百个旅行预订网站收集数据，而不触发反机器人措施。",
          "Competitive analysis": "使用住宅代理监控不同地理市场的竞争对手定价策略和市场趋势。"
        },
        "market-research": {
          "Competitor monitoring": "使用住宅代理跨多个市场和平台跟踪竞争对手的定价、库存和营销策略。",
          "Price tracking": "使用住宅代理实时监控电商平台的价格变化，识别趋势和机会。",
          "Inventory checking": "使用住宅代理验证不同地区和市场渠道的产品可用性和库存水平。",
          "Market analysis": "使用住宅代理收集全面的市场情报，包括评论、评级和消费者情感数据。"
        },
        "google-proxies": {
          "Google-optimized": "专为谷歌服务优化的专用住宅代理基础设施，具有高成功率和最少封锁。",
          "SERP data collection": "使用维护搜索结果完整性和排名数据的住宅代理准确提取搜索引擎结果页面。",
          "Ad verification": "使用住宅代理验证广告投放并监控不同地理市场的竞争对手广告策略。",
          "SEO monitoring": "使用住宅代理从多个位置和设备跟踪关键词排名、搜索可见性和SEO表现。"
        },
        "access-restricted-content": {
          "Bypass geo-blocks": "使用来自您需要的特定国家的住宅代理访问区域锁定的内容和服务。",
          "Content verification": "使用住宅代理验证您的内容在不同市场中是否正确显示，并测试区域合规要求。",
          "Global access": "通过使用住宅代理从任何地理位置访问内容和服务，为您的应用程序提供全球覆盖。",
          "Streaming support": "使用住宅代理访问流媒体平台和媒体内容，用于研究、测试或内容验证目的。"
        },
        "privacy-advantages": {
          "Complete anonymity": "使用与普通用户无法区分的住宅代理完全私密地浏览互联网。",
          "Identity protection": "使用住宅代理保护敏感的商业情报操作和研究活动，防止被追溯到您的组织。",
          "Secure browsing": "在访问潜在风险或受监控的网站和平台时使用住宅代理保持安全和隐私。",
          "Business intelligence": "使用住宅代理进行竞争研究和市场情报收集，而不透露您的企业身份。"
        }
      },
      useCases: {
        "proxies-for-ai": {
          title: "AI代理",
          description: "使用可靠的住宅代理扩展AI训练和数据收集",
          longDescription: "利用我们的企业级住宅代理基础设施为您的AI和机器学习项目提供支持。使用看起来像真实用户的住宅IP地址访问多样化的数据源，无地理限制或速率限制。",
          benefits: ["无限数据收集", "绕过AI检测", "扩展ML训练", "全球数据多样性"]
        },
        "web-scraping": {
          title: "网页抓取",
          description: "使用轮换住宅代理大规模提取数据",
          longDescription: "通过我们先进的住宅代理轮换从任何网站提取数据。使用我们的住宅IP地址池避免封锁和验证码，这些IP地址看起来像合法的用户流量。",
          benefits: ["避免封锁和禁令", "高成功率", "自动轮换IP", "解析任何网站"]
        },
        "website-testing": {
          title: "网站测试",
          description: "使用来自不同地理位置的住宅代理测试网站和应用程序",
          longDescription: "使用住宅代理确保您的网站和应用程序在任何位置都能完美运行。使用来自200+个国家的真实住宅IP地址测试地理限制内容和本地化体验。",
          benefits: ["多位置测试", "性能监控", "地理封锁检测", "用户体验验证"]
        },
        "travel-fare-aggregation": {
          title: "旅行价格聚合",
          description: "使用住宅代理比较各旅行预订平台的价格和可用性",
          longDescription: "使用住宅代理从航空公司、酒店和预订平台获取实时旅行定价数据。通过使用住宅IP地址访问网站来防止价格歧视，为您的客户提供准确的价格比较。",
          benefits: ["实时定价", "避免价格歧视", "多平台", "竞争分析"]
        },
        "market-research": {
          title: "市场研究",
          description: "使用住宅代理大规模收集市场情报和竞争对手数据",
          longDescription: "使用住宅代理收集全面的市场数据和竞争对手情报。使用住宅IP地址监控全球市场的定价、库存和市场趋势，确保准确、无偏见的数据收集。",
          benefits: ["竞争对手监控", "价格跟踪", "库存检查", "市场分析"]
        },
        "google-proxies": {
          title: "谷歌代理",
          description: "专为谷歌服务和SERP数据优化的专用住宅代理",
          longDescription: "使用我们专门优化的住宅代理无限制访问谷歌搜索、广告、购物和其他谷歌服务。使用保持高成功率的住宅IP地址专为SERP抓取和SEO监控优化。",
          benefits: ["谷歌优化", "SERP数据收集", "广告验证", "SEO监控"]
        },
        "access-restricted-content": {
          title: "访问受限内容",
          description: "使用住宅代理绕过地理限制，从任何地方访问内容",
          longDescription: "使用住宅代理访问地理限制内容、流媒体平台和区域锁定网站。使用来自您需要的特定国家的住宅IP地址，非常适合内容验证和全球市场研究。",
          benefits: ["绕过地理封锁", "内容验证", "全球访问", "流媒体支持"]
        },
        "privacy-advantages": {
          title: "隐私优势",
          description: "使用住宅代理保护您的身份并在线保持匿名",
          longDescription: "使用住宅代理在浏览时保持完全的隐私和匿名性。使用与普通用户无法区分的住宅IP地址保护敏感的商业情报操作和研究活动。",
          benefits: ["完全匿名", "身份保护", "安全浏览", "商业情报"]
        }
      }
    },

    // Pricing Page - 价格页面
    pricingPage: {
      mostPopular: "最受欢迎",
      from: "起价",
      month: "月",
      save: "节省",
      contactSales: "联系销售",
      getStarted: "开始使用",
      residentialProxy: "住宅代理",
      mobileProxy: "移动代理",
      simpleTransparent: "简单透明的",
      pricing: "定价",
      residentialSubtitle: "使用我们的优质住宅代理网络安心扩展业务。批量折扣高达31%。",
      mobileSubtitle: "访问真实移动IP与运营商轮换。非常适合移动应用测试和社交媒体自动化。",
      generalSubtitle: "选择适合您需求的完美方案。无隐藏费用，无设置成本。提供批量折扣。",
      uptimeSLA: "99.9%正常运行时间SLA",
      happyCustomers: "10,000+满意客户",
      instantActivation: "即时激活",
      pricingCalculator: "价格计算器",
      calculatorSubtitle: "根据使用情况计算您的预估月度成本",
      faq: {
        title: "常见问题",
        subtitle: "关于我们定价的所有信息",
        questions: [
          {
            question: "批量定价如何运作？",
            answer: "我们的住宅代理定价随批量递减：1-30GB为$1.30/GB，30-100GB为$1.10/GB，100GB-1TB为$1.00/GB，1-5TB为$0.90/GB。企业客户5TB+可享受定制价格。"
          },
          {
            question: "你们提供免费试用吗？",
            answer: "是的！我们为移动代理提供24小时试用，价格为$10/代理。对于住宅代理，请联系我们的销售团队，根据您的需求获得定制试用。"
          },
          {
            question: "你们接受哪些付款方式？",
            answer: "我们接受所有主要信用卡、PayPal、电汇和加密货币。企业客户还可以通过发票付款，享受NET30条款。"
          },
          {
            question: "我可以随时更改方案吗？",
            answer: "当然可以！您可以随时升级或降级方案。更改立即生效，我们会按比例调整任何账单差额。"
          },
          {
            question: "企业方案包括什么？",
            answer: "企业方案包括高达50%的批量折扣、专属客户管理、SLA保证、定制合同、合规支持和优先技术支持。"
          }
        ]
      },
      cta: {
        title: "准备开始了吗？",
        subtitle: "加入数千家使用IP9可靠代理基础设施的企业。从我们最受欢迎的方案开始，或联系销售获取定制解决方案。",
        startPlan: "立即开始",
        contactSales: "联系销售团队",
        noSetupFees: "无设置费用",
        cancelAnytime: "随时取消",
        support247: "24/7支持"
      }
    },

    // About Page - 关于我们页面
    aboutPage: {
      title: "关于IP9",
      heroDescription: "我们正在构建世界上最可靠的代理基础设施，为下一代数据驱动的企业提供动力。",
      stats: {
        ipAddresses: "IP地址",
        countries: "国家",
        happyCustomers: "满意客户",
        uptimeSLA: "正常运行时间SLA"
      },
      story: {
        title: "我们的故事",
        subtitle: "基于民主化可靠代理基础设施访问的使命而成立",
        paragraph1: "IP9成立于2020年，由一个基础设施工程师团队创立，他们亲身体验了大规模构建可靠数据收集系统的挑战。由于对现有代理服务提供商的局限性感到沮丧，我们决心构建更好的解决方案。",
        paragraph2: "如今，我们运营着世界上最大的住宅代理网络，在200多个国家拥有超过3亿个IP地址。我们的基础设施为从AI训练和市场研究到电子商务监控和旅行聚合的各种应用提供支持。",
        paragraph3: "我们自豪地为全球超过10,000家企业提供服务，从初创公司到财富500强企业，帮助他们收集数据、测试应用程序，并以信心和可靠性扩展到全球。"
      },
      values: {
        title: "我们的价值观",
        subtitle: "指导我们在IP9所做一切的原则",
        securityFirst: {
          title: "安全第一",
          description: "我们在所做的一切中都优先考虑安全和隐私，具有SOC2合规性和企业级基础设施。"
        },
        globalReach: {
          title: "全球覆盖",
          description: "我们的全球网络确保从任何位置都能可靠访问，具有全面的覆盖和定位选项。"
        },
        customerSuccess: {
          title: "客户成功",
          description: "专门的支持团队和客户经理确保您的成功，提供24/7技术支持。"
        },
        innovation: {
          title: "创新",
          description: "在代理技术和基础设施方面持续创新，以保持领先于行业需求。"
        }
      },
      cta: {
        title: "准备与我们合作吗？",
        subtitle: "加入数千家信任IP9代理基础设施需求的企业。",
        startFreeTrial: "立即开始",
        contactSales: "联系销售"
      }
    },

    // Common - 通用
    common: {
      learnMore: "了解更多",
      getStarted: "开始使用",
      readMore: "阅读更多",
      backToHome: "返回首页",
      loading: "加载中...",
      error: "出现错误",
      tryAgain: "重试"
    },

    // Documentation - 文档
    documentation: {
      searchPlaceholder: "搜索文档...",
      menuToggle: "文档菜单",
      notFound: {
        title: "页面未找到",
        description: "您要查找的文档页面不存在。",
        backToDocumentation: "返回文档"
      },
      sections: {
        residentialProxies: {
          title: "住宅代理",
          description: "使用IP9住宅代理进行网页抓取、数据收集和自动化的完整指南。",
          keyFeatures: "主要功能",
          quickStart: "快速开始",
          authenticationRequired: "需要身份验证",
          authenticationNote: "所有请求必须包含您的用户名和密码。您可以在IP9控制台中找到这些凭据。",
          features: [
            "超过3亿个住宅IP地址",
            "覆盖200多个国家和1000多个城市",
            "99.9%正常运行时间和高速连接",
            "会话控制和粘性会话",
            "按国家、城市、州和ASN进行高级定位",
            "自动IP轮换"
          ],
          content: {
            introText1: "IP9住宅代理包含由互联网服务提供商（ISP）提供的真实IP地址。这些IP地址在全球范围内按国家或城市级别连接到物理设备。因此，使用这种类型的代理可以轻松复制人类行为并避免封锁。",
            introText2: "IP9住宅代理功能齐全，自动轮换，易于设置。我们的网络提供来自全球200多个国家和1000多个城市的超过3亿个住宅IP地址。",
            quickStartText: "几分钟内即可开始使用住宅代理。使用 https://httpbin.org/ip 测试您的连接：",
            headings: {
              keyFeatures: "主要功能",
              quickStart: "快速开始"
            },
            features: [
              "超过3亿个住宅IP地址",
              "覆盖200多个国家和1000多个城市",
              "99.9%正常运行时间和高速连接",
              "会话控制和粘性会话",
              "按国家、城市、州和ASN进行高级定位",
              "自动IP轮换"
            ],
            notes: {
              authenticationRequired: "需要身份验证",
              authenticationNote: "所有请求必须包含您的用户名和密码。您可以在IP9控制台中找到这些凭据。"
            }
          }
        },
        gettingStarted: {
          title: "开始使用",
          description: "了解设置和使用IP9住宅代理的基础知识。",
          content: {
            introText1: "通过我们的反向连接入口点，使用来自随机位置的住宅代理向 https://httpbin.org/ip 发起请求。将USERNAME和PASSWORD替换为您的代理用户凭据。请记住，每个请求都会返回一个新的IP地址，因为IP9住宅代理会自动轮换。",
            introText2: "使用 httpbin.org/ip 检查您的IP参数——此域名提供包括IP地址、提供商、国家、城市和其他地理位置数据在内的信息。",
            basicSetupIntro: "我们的住宅代理支持HTTP协议：",
            countryTargetingIntro: "现在使用来自法国的代理发起请求。您只需在用户名字符串中添加country-FR：",
            cityTargetingIntro: "这是一个通过英国伦敦代理发起请求的示例。在用户名字符串中添加country-GB-city-london可以指定城市：",
            sessionControlIntro: "如果您需要为多个请求保持相同的代理，请使用会话控制。在用户名字符串中添加session-randomString123，其中randomString123可以是任何随机字符串：",
            supportContact: "如果您有任何其他问题，请通过support@ip9.io联系我们的支持团队。",
            headings: {
              basicSetup: "基本设置",
              countryTargeting: "国家定位",
              cityLevelTargeting: "城市级定位",
              sessionControl: "会话控制"
            },
            notes: {
              dashboardAccess: "控制台访问",
              dashboardNote: "要了解您的使用统计、生成的流量、请求数量等信息，请登录您的IP9控制台。您还可以添加代理用户（子用户）并单独跟踪他们的统计信息。"
            }
          }
        },
        makingRequests: {
          title: "发起请求",
          description: "通过IP9住宅代理发起HTTP请求的详细指南。",
          content: {
            basicQueryIntro: "基本查询只需要传递用户名和密码。不需要其他参数。这样的查询将导致请求从随机IP地址（代理）发出。每个新请求都将使用不同的代理。",
            codeExampleIntro: "在此示例中，从随机IP对httpbin.org/ip执行查询：",
            advancedConfigIntro: "单个反向连接代理（residential.ip9.io:8000）使您能够通过用户名中的附加参数选择特定的国家或城市代理。此方法还支持会话控制。以下是凭据示例：",
            headings: {
              basicQuery: "基本查询",
              codeExamples: "代码示例",
              advancedConfiguration: "高级配置",
              queryParameters: "查询参数"
            },
            tableHeaders: ["参数", "描述"],
            parameterDescriptions: {
              username: "您的IP9用户名",
              country: "不区分大小写的2字母ISO 3166-1 alpha-2格式国家代码。例如，德国为DE，英国为GB，泰国为TH。",
              city: "不区分大小写的英文城市名称。此参数必须伴随国家以获得更好的准确性，例如，英国伦敦为country-GB-city-london。",
              state: "不区分大小写的美国州名称，以us_开头，例如us_california、us_illinois。",
              session: "会话ID，用于在即将到来的查询中保持相同的IP。会话在10分钟后过期。之后，将为该会话ID分配新的IP地址。",
              sesstime: "会话时间参数在特定时间内保持相同的IP。最大会话时间为30分钟。",
              password: "您的IP9密码（必需）"
            }
          }
        },
        locationSettings: {
          title: "位置设置",
          description: "为特定国家、城市和地区配置代理位置定位。",
          content: {
            introText1: "IP9住宅代理网络覆盖200多个位置，提供国家、城市、州、大陆、邮政编码、坐标级别和ASN定位。",
            introText2: "您可以在下面列出的页面中找到有关选择地理位置参数的更多信息：",
            mappingIntro: "我们使用MaxMind GeoIP2数据库为我们的出口节点分配国家、州和城市值。此数据库每周更新。",
            mappingNote: "一些其他公共IP数据库可能显示不同的位置。在不能有任何位置不匹配的情况下，请仔细检查分配代理的位置是否与目标网站使用的IP数据库相同。",
            supportContact: "如果您有任何其他问题，请联系您的客户经理或我们的支持团队support@ip9.io。",
            listItems: [
              "国家 - 定位特定国家",
              "城市 - 定位国家内的特定城市",
              "州 - 专门定位美国各州",
              "大陆 - 定位整个大陆",
              "邮政编码 - 超精确邮政编码定位",
              "坐标 - 定位特定地理坐标",
              "ASN定位 - 定位特定互联网服务提供商"
            ],
            headings: {
              countryStateCityMapping: "国家、州、城市映射"
            }
          }
        },
        country: {
          title: "国家",
          description: "查找国家参数和特定国家入口节点。",
          content: {
            introText: "在授权头中添加国家标志可以指定使用哪个国家的IP来处理请求。此参数的值是不区分大小写的2字母ISO 3166-1 alpha-2格式国家代码。例如，德国为DE，英国为GB，泰国代理为TH。",
            exampleIntro: "以下是一些国家参数的示例：",
            codeExampleIntro: "在此示例中，从德国的随机IP地址对httpbin.org/ip执行查询：",
            headings: {
              codeExample: "代码示例"
            },
            tableHeaders: ["国家", "国家参数"],
            countryNames: {
              "United States": "美国",
              "Italy": "意大利", 
              "Thailand": "泰国",
              "Japan": "日本",
              "Australia": "澳大利亚",
              "Spain": "西班牙"
            },
            countrySpecificNodes: "国家特定入口节点",
            nodeDescription: "IP9住宅网络几乎覆盖世界上每个国家。随机的国家特定代理入口点将在每个新请求中返回新的IP。",
            authDescription: "您无需传递任何其他参数，只需用户名:密码。如果您已将IP列入白名单，则无需传递登录凭据。国家特定端口不支持城市级结果。",
            entryPointsNote: "请查看您的IP9仪表板以获取完整的入口点信息和下载选项。"
          }
        },
        city: {
          title: "城市",
          description: "定位特定城市以进行精确位置定位。",
          content: {
            introText: "添加城市参数可以缩小到城市级定位。例如，country-DE-city-munich意味着来自德国慕尼黑的代理将处理查询。",
            coverageText: "我们支持世界上的每个城市，但由于住宅代理的动态特性，我们不保证在特定时刻会有代理。最受欢迎的城市覆盖良好。",
            exampleIntro: "以下是国家和城市参数有效组合的一些示例：",
            codeExampleIntro: "在此示例中，从德国慕尼黑的随机IP地址对httpbin.org/ip执行查询：",
            headings: {
              codeExamples: "代码示例"
            },
            cityExamples: [
              "country-US-city-los_angeles",
              "country-IT-city-rome", 
              "country-TH-city-bangkok",
              "country-JP-city-tokyo",
              "country-AU-city-sydney",
              "country-ES-city-barcelona"
            ],
            cityListNote: "联系我们的支持团队support@ip9.io获取支持城市的完整列表，或检查您的仪表板以了解城市可用性。",
            cityListTitle: "城市列表"
          }
        },
        state: {
          title: "州",
          description: "使用住宅代理定位特定美国州。",
          content: {
            introText: "要从特定美国州获取代理，请在授权字符串中添加州标志。例如，state-us_california、state-us_illinois。",
            exampleIntro: "以下是州参数有效组合的一些示例：",
            stateExamples: [
              "state-us_california-city-los_angeles",
              "state-us_illinois-city-springfield",
              "state-us_michigan-city-detroit",
              "state-us_ohio-city-columbus",
              "state-us_maine-city-portland"
            ],
            stateCodeExampleIntro: "在此示例中，从美国加利福尼亚州的随机IP地址对httpbin.org/ip执行查询：",
            headings: {
              codeExample: "代码示例"
            },
            stateListNote: "联系我们的支持团队support@ip9.io获取支持美国州的完整列表。",
            stateListTitle: "美国州列表"
          }
        },
        continent: {
          title: "大陆",
          description: "定位整个大陆以获得广泛的地理覆盖。",
          content: {
            introText: "大陆定位允许您通过来自整个大陆的代理路由请求，以获得更广泛的地理覆盖。当您需要区域多样性而不指定确切国家时，这很有用。",
            headings: {
              supportedContinents: "支持的大陆"
            },
            continents: [
              "北美洲 (NA)",
              "欧洲 (EU)", 
              "亚洲 (AS)",
              "南美洲 (SA)",
              "非洲 (AF)",
              "大洋洲 (OC)"
            ]
          }
        },
        zipPostalCode: {
          title: "邮政编码",
          description: "使用邮政编码进行超精确定位。",
          content: {
            introText: "邮政编码定位提供最精确的地理定位，允许您通过特定邮政编码区域的代理路由请求。",
            precisionText: "此定位方法适用于支持的国家，并提供尽可能高的地理精度。",
            availabilityTitle: "可用性",
            availabilityNote: "邮政编码定位取决于特定区域的代理可用性。请联系我们的支持团队support@ip9.io获取覆盖信息。"
          }
        },
        coordinates: {
          title: "坐标",
          description: "定位特定地理坐标。",
          content: {
            introText: "坐标定位允许您指定确切的纬度和经度坐标，以获得最精确的地理定位。",
            advancedText: "当您需要基于精确坐标的非常特定地理位置的代理时，此高级定位方法很有用。",
            coordinateNote: "坐标定位取决于指定坐标附近的代理可用性。结果可能因网络覆盖范围而异。",
            precisionNote: "您可以在每个请求中使用国家或ASN参数。如果同时使用两个位置参数，只会应用国家参数。",
            precisionTitle: "精度"
          }
        },
        asnTargeting: {
          title: "ASN定位",
          description: "使用ASN号码定位特定互联网服务提供商。",
          content: {
            introText: "我们的住宅代理支持按ASN号码定位，这意味着您可以从特定运营商选择代理。您必须在请求中输入所需的ASN号码。",
            exampleIntro: "以下是T-Mobile的示例，其在美国的ASN号码为21928：",
            lookupText: "您可以使用在线ASN查找工具找到ASN号码。住宅代理池本质上是动态的，因此如果在那个时刻该特定ASN下没有IP，您将收到502 Bad Gateway响应代码。在这种情况下，您应该尝试另一个ASN号码。",
            importantTitle: "重要提示",
            importantNote: "您可以在每个请求中使用国家或ASN参数。如果同时使用两个位置参数，只会应用国家参数。"
          }
        },
        sessionControl: {
          title: "会话控制",
          description: "了解如何在多个请求中保持相同的IP地址。",
          content: {
            establishingSession: "建立会话",
            sessionIntro: "会话ID参数允许您保持相同的IP地址来运行多个请求。要多次重用同一IP，请在用户名后使用会话参数和随机创建的字母数字字符串，例如session-abcde12345。",
            sessionDuration: "标准会话时间为10分钟或最多60秒不活动（无请求）。之后，会自动分配新的IP地址。",
            extendedSession: "扩展会话",
            extendedIntro: "使用sesstime参数可以将会话延长到最多30分钟。这对于需要较长稳定连接的任务很有用。",
            sessionTimeText: "您可以使用sesstime参数将相同IP保持最多1440分钟（24小时）。但是，由于住宅代理池是动态的，您的连接可能会更早结束。",
            headings: {
              establishingSession: "建立会话",
              extendedSession: "扩展会话",
              bestPractices: "最佳实践",
              sessionTime: "会话时间"
            }
          }
        },
        protocols: {
          title: "协议",
          description: "IP9住宅代理的HTTP、HTTPS和SOCKS5协议支持。",
          content: {
            introText: "IP9住宅代理支持HTTP协议。",
            httpIntro: "我们的文档包括展示如何使用HTTP协议连接到代理的代码示例。该协议受常见库和第三方软件支持。",
            httpsIntro1: "您还可以使用HTTPS协议的完全加密连接来获得额外的安全层。为此，您必须在代理入口点添加https://。例如：https://residential.ip9.io:8001（这也适用于国家特定的入口节点）。",
            httpsIntro2: "请注意，某些库（或其较旧版本）和某些第三方工具可能不支持HTTPS协议，因此在更改代码之前请仔细检查。",
            socks5Intro1: "SOCKS5 UDP连接现已在测试版中可用。请联系您的客户经理或客户成功团队以启用测试。",
            socks5Intro2: "IP9住宅代理目前仅支持TCP连接。要使用SOCKS5协议，您必须在请求中添加socks5h，例如：",
            socks5Intro3: "请注意，目前SOCKS5协议不支持国家特定的入口节点。您可以通过在用户名中添加其他参数来定位特定位置。以下是定位法国的示例：",
            portsIntro: "代理端口的默认配置包括80和443，这些是为适应HTTP和HTTPS协议而设计的标准端口，如下表所示。要访问其他端口，需要进行合规验证，请联系我们的支持团队support@ip9.io。",
            headings: {
              http: "HTTP",
              https: "HTTPS", 
              socks5: "SOCKS5",
              portsOfProxies: "代理端口"
            },
            notes: {
              socks5Important: "重要提示",
              socks5Warning: "使用SOCKS5协议时，某些网站可能会识别代理IP。我们强烈建议在这种情况下切换到替代协议。"
            },
            tableHeaders: ["协议", "端口"]
          }
        },
        restrictedTargets: {
          title: "受限目标",
          description: "有关可能有限制的网站和服务的信息。",
          content: {
            introText: "在我们的IP9住宅代理网络上，一些网站受到限制，以避免滥用和不明确的活动。",
            kycText: "一些目标可以在经过简短的KYC程序后解除封锁（包括Google域名）。如果您的目标属于上述组别，请联系我们的客户支持团队support@ip9.io，以确保您在购买前可以访问目标。",
            payAsYouGoText: "按需付费不可退款，即使目标不受支持。请在购买前与客户成功团队确认兼容性。",
            contactText: "通过support@ip9.io联系我们的客户支持团队或通过我们网站上的24/7实时聊天。",
            importantNote: "重要提示",
            listNote: "该列表包括但不限于以下目标：",
            ipCheckingNote: "IP检查",
            ipCheckingText: "*使用IP9代理时，请使用httpbin.org/ip检查您的IP地址。",
            headings: {
              restrictedTargetCategories: "受限目标类别",
              importantNotice: "重要通知"
            },
            tableHeaders: ["目标类别", "示例"],
            categories: [
              ["娱乐和流媒体", "Netflix, Spotify, Twitch, Disney"],
              ["银行和其他金融机构", "PayPal, Bank of America, Binance"],
              ["政府网站", "usa.gov, canada.ca"],
              ["游戏", "Playstation, Steam"],
              ["票务", "Ticketmaster, Eventbrite"],
              ["邮件", "Outlook, Yahoo mail"],
              ["广告", "Google Tag Manager, Doubleclick"],
              ["IP检查网站*", "ipinfo.info, iplocation.net"]
            ]
          }
        }
      }
    }
  }
};